import { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { UserGuides } from "@/components/UserGuides";
import { toast } from "sonner";
import { LifeBuoy, ChevronDown, PlayCircle, Lightbulb, Video, Check } from "lucide-react";

// Convert YouTube/Loom links to embeddable URLs; mp4 plays natively.
function embedUrl(url) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const loom = url.match(/loom\.com\/share\/([\w-]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  return null;
}

function VideoBlock({ topic, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(topic.video_url || "");
  const save = async () => {
    try {
      await client.post(`/help/${topic.key}/video`, { url });
      toast.success("Tutorial video saved");
      setEditing(false); onSaved();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
  };
  const emb = topic.video_url ? embedUrl(topic.video_url) : null;
  return (
    <div className="mt-3">
      {topic.video_url ? (
        emb ? (
          <iframe data-testid={`help-video-${topic.key}`} src={emb} title={topic.title} allowFullScreen
            className="w-full aspect-video border border-[#2C2D35] rounded-sm" />
        ) : topic.video_url.match(/\.(mp4|webm|mov)(\?|$)/i) ? (
          <video data-testid={`help-video-${topic.key}`} src={topic.video_url} controls className="w-full aspect-video border border-[#2C2D35] rounded-sm bg-black" />
        ) : (
          <a data-testid={`help-video-${topic.key}`} href={topic.video_url} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-sm text-[#22D3EE] hover:underline"><PlayCircle size={16} /> Watch tutorial video</a>
        )
      ) : (
        <div className="flex items-center gap-2 font-mono text-[11px] text-[#6B7280] border border-dashed border-[#2C2D35] p-3 rounded-sm">
          <Video size={14} /> No tutorial video attached yet{canEdit ? " — paste a YouTube, Loom or MP4 link below." : "."}
        </div>
      )}
      {canEdit && (editing ? (
        <div className="flex gap-1.5 mt-2">
          <input data-testid={`help-video-input-${topic.key}`} value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=…  ·  https://loom.com/share/…  ·  https://…/video.mp4"
            className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-1.5 text-xs font-mono text-white outline-none focus:border-[#22D3EE] rounded-sm" />
          <button data-testid={`help-video-save-${topic.key}`} onClick={save} className="px-3 py-1.5 text-[11px] font-head font-bold bg-[#22D3EE] text-black rounded-sm flex items-center gap-1"><Check size={12} /> SAVE</button>
        </div>
      ) : (
        <button data-testid={`help-video-edit-${topic.key}`} onClick={() => setEditing(true)}
          className="mt-2 text-[10px] font-mono text-[#22D3EE] hover:underline">{topic.video_url ? "Change video link" : "+ Attach video link"}</button>
      ))}
    </div>
  );
}

export default function Help() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState("call_taker");
  const canEdit = ["admin", "supervisor", "watch_commander"].includes(user?.role);
  const load = () => client.get("/help").then((r) => setTopics(r.data.topics)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <Layout>
      <PageHeader icon={LifeBuoy} title="HELP CENTER" subtitle="HOW-TO GUIDES & TUTORIAL VIDEOS · EVERY MODULE OF THE CAD / MDT / RMS PLATFORM" />
      <div className="p-4 max-w-4xl" data-testid="help-center">
        <UserGuides />
        <div className="flex items-center gap-2 mb-2.5">
          <LifeBuoy size={15} className="text-[#22D3EE]" />
          <span className="font-head font-bold text-xs text-white tracking-wider">MODULE HOW-TO GUIDES</span>
        </div>
        <div className="space-y-2.5">
        {topics.map((t) => {
          const isOpen = open === t.key;
          return (
            <div key={t.key} className="border border-[#2C2D35] bg-[#141518] rounded-sm">
              <button data-testid={`help-topic-${t.key}`} onClick={() => setOpen(isOpen ? "" : t.key)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <span className="font-mono text-[9px] font-bold uppercase px-2 py-0.5 border border-[#22D3EE]/50 text-[#22D3EE] rounded-sm shrink-0">{t.module}</span>
                <span className="font-head font-bold text-sm text-white">{t.title}</span>
                {t.video_url && <PlayCircle size={14} className="text-[#4ade80] shrink-0" title="Video available" />}
                <ChevronDown size={15} className="ml-auto text-[#9CA3AF] transition-transform" style={{ transform: isOpen ? "rotate(180deg)" : "none" }} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4" data-testid={`help-body-${t.key}`}>
                  <ol className="space-y-1.5 list-decimal list-inside">
                    {t.steps.map((s, i) => <li key={i} className="text-sm text-[#C9CDD4] leading-relaxed">{s}</li>)}
                  </ol>
                  {(t.tips || []).length > 0 && (
                    <div className="mt-3 border-l-2 border-[#F59E0B] bg-[#1a140a] p-2.5 space-y-1">
                      {t.tips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-[#F59E0B]"><Lightbulb size={13} className="mt-0.5 shrink-0" /> {tip}</div>
                      ))}
                    </div>
                  )}
                  <VideoBlock topic={t} canEdit={canEdit} onSaved={load} />
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </Layout>
  );
}
